import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ImageViewerImage } from "@omni/types/api/product/bff/shared/imageViewerImage.model";
import { ImageViewerVideo } from "@omni/types/api/product/bff/shared/imageViewerVideo.model";
import { ProductImageRendition } from "@omni/types/api/product/shared/enums/productImageRendition.enum";
import { Media } from "@omni/types/api/shared/bff/media.model";

const mapMediaItem = (
    mediaList: Media[],
    altTextFallback: string
): ImageViewerImage | null => {
    let altText;
    const imageViewerImage: Partial<ImageViewerImage> = {};

    mediaList.forEach((media) => {
        altText = media.altText;

        switch (media.format) {
            case ProductImageRendition.MINI:
                imageViewerImage.thumbnail = media.url;
                break;

            case ProductImageRendition.MD:
                imageViewerImage.main = media.url;
                break;

            case ProductImageRendition.LG:
                imageViewerImage.zoom = media.url;
                break;

            case ProductImageRendition.XL:
                imageViewerImage.maxZoom = media.url;
                break;
        }
    });

    imageViewerImage.altText = altText || altTextFallback;

    if (
        imageViewerImage.main &&
        imageViewerImage.thumbnail &&
        imageViewerImage.altText
    ) {
        return imageViewerImage as ImageViewerImage;
    }

    return null;
};

const isYouTubeVideo = (url?: string) => {
    return (
        url &&
        new RegExp(
            /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/g
        ).test(url)
    );
};

const isVimeoVideo = (url?: string) => {
    return url && url.indexOf("vimeo") > -1;
};

const getYouTubeId = (url: string) => {
    const arr = url.split(/(vi\/|v%3D|v=|\/v\/|youtu\.be\/|\/embed\/)/);
    return undefined !== arr[2] ? arr[2].split(/[^\w-]/i)[0] : arr[0];
};

const getVimeoId = (url: string) => {
    const VIMEO_CODE_SPLIT = "vimeo.com/";
    const splitIndex = url.indexOf(VIMEO_CODE_SPLIT);

    if (splitIndex > -1) {
        return url.substring(splitIndex + VIMEO_CODE_SPLIT.length);
    }

    return undefined;
};

const ImageViewerAdapter = (product: Product<ProductFieldSet.Full>) => {
    const images: ImageViewerImage[] = [];
    let videos: ImageViewerVideo[] = [];
    let usesDefaultImage = "0";

    if (product.mainImages) {
        product.mainImages.forEach((mainImage, index) => {
            const imageViewerImage = mapMediaItem(
                mainImage.medias,
                `${product.productTitle} ${index + 1}`
            );
            if (imageViewerImage) images.push(imageViewerImage);
        });
    }

    if (product.additionalImages) {
        product.additionalImages.forEach((mainImage, index) => {
            const imageViewerImage = mapMediaItem(
                mainImage.medias,
                `${product.productTitle} ${index + 1}`
            );
            if (imageViewerImage) images.push(imageViewerImage);
        });
    }

    if (
        images.length === 0 &&
        product.mainImages.length === 1 &&
        product.mainImages[0].medias.length > 0
    ) {
        const cloudMediaElement = product.mainImages[0].medias[0];
        images.push({
            thumbnail: cloudMediaElement.url,
            main: cloudMediaElement.url,
            altText: cloudMediaElement.altText
        });
        usesDefaultImage = "1";
    }

    videos = (product.videos || [])
        .filter((v) => v.name)
        .reduce(
            (acc, video) => {
                if (isYouTubeVideo(video.url)) {
                    const videoId = getYouTubeId(video.url);

                    acc.push({
                        videoId,
                        altText: video.name,
                        main: `https://www.youtube.com/embed/${videoId}`,
                        thumbnail: `https://img.youtube.com/vi/${videoId}/3.jpg`
                    });
                } else if (isVimeoVideo(video.url)) {
                    const videoId = getVimeoId(video.url);
                    if (videoId) {
                        acc.push({
                            videoId,
                            altText: video.name,
                            main: `https://player.vimeo.com/video/${videoId}`
                        });
                    }
                }

                return acc;
            },
            [] as Array<ImageViewerVideo>
        );

    return {
        sku: product.sku,
        usesDefaultImage: usesDefaultImage as "0" | "1",
        images,
        videos
    };
};

export { ImageViewerAdapter };
